// A sandbox's stored events (`/admin/sandboxes/{id}/events`) read back as the turns the user's pane would have drawn:
// the message sent, the text and the steps in arrival order, each step's status and duration, the plans, and the
// turn's clock — paused while it waits on staff, as the add-in's pauses while it waits on the user (ChatSession
// `turnClock`). Times come from each event's `created_at`, not from the browser's clock.
import type { SandboxEvent } from '../../types/api';
import { SERVER_TOOL_NAMES, toolLabel, toolTarget } from './tools';

export type StepStatus = 'pending' | 'waiting' | 'running' | 'done' | 'error' | 'cancelled';

export interface Step {
  id: string;
  name: string;
  label: string;
  target: string | null;
  status: StepStatus;
  /** Run by the server (a read, an operation) rather than in Excel — the simulator here. */
  server: boolean;
  startedAt: number;
  ms: number | null;
  error: string | null;
  input: Record<string, unknown>;
}

export type Segment = { kind: 'text'; text: string } | { kind: 'steps'; steps: Step[] };

export type TurnState = 'running' | 'waiting' | 'done' | 'failed' | 'cancelled';
export type Activity = 'thinking' | 'reading' | 'planning' | 'excel';

export interface Plan {
  operation?: string;
  name?: string;
  calls?: number;
  markdown?: string;
}

export interface Fidelity {
  unsupported: Record<string, unknown[]>;
  loops: unknown[];
}

export interface SandboxTurn {
  index: number;
  /** The recorded turn it sent, or null for a message staff typed. */
  recorded: number | null;
  mode: string | null;
  message: string;
  indexVersion: number | null;
  startedAt: number;
  endedAt: number | null;
  /** Time spent waiting on staff (questions, confirmations), left out of the turn's clock. */
  waitMs: number;
  waitingSince: number | null;
  state: TurnState;
  segments: Segment[];
  plans: Plan[];
  fidelity: Fidelity | null;
  committedVersion: number | null;
  error: string | null;
}

// The add-in's (`MessageBubble.tsx` READ_TOOLS, PLAN_TOOLS): what makes its activity line say "Reading the model…" or
// "Planning the change…" rather than "Thinking…".
const READ_TOOLS = new Set(['find', 'outline', 'describe_sheet', 'read_zone', 'read_range', 'trace', 'zone_links',
  'resolve', 'resolve_object', 'period_map', 'siblings', 'placement', 'conventions', 'health']);
const PLAN_TOOLS = new Set(['start_add', 'start_edit', 'start_delete', 'submit_change_plan']);

function parse<T>(data: string): T | null {
  try {
    return JSON.parse(data) as T;
  } catch {
    return null;
  }
}

function lastSteps(turn: SandboxTurn): Step[] {
  const last = turn.segments[turn.segments.length - 1];
  if (last?.kind === 'steps') return last.steps;
  const seg: Segment = { kind: 'steps', steps: [] };
  turn.segments.push(seg);
  return seg.steps;
}

function findStep(turn: SandboxTurn, id: string): Step | undefined {
  for (const seg of turn.segments) {
    if (seg.kind !== 'steps') continue;
    const step = seg.steps.find((s) => s.id === id);
    if (step) return step;
  }
  return undefined;
}

function settle(step: Step, status: StepStatus, at: number, ms?: number | null, error?: string | null) {
  step.status = status;
  step.ms = ms ?? Math.max(0, at - step.startedAt);
  step.error = error ?? null;
}

/** The turns of a sandbox, from its events in order. */
export function replay(events: SandboxEvent[]): SandboxTurn[] {
  const turns: SandboxTurn[] = [];
  const current = (): SandboxTurn | undefined => turns[turns.length - 1];
  for (const e of events) {
    const at = Date.parse(e.created_at);
    if (e.event === 'turn_started') {
      const d = parse<{ recorded: number | null; mode: string | null; index_version: number | null; message?: string }>(e.data) ?? { recorded: null, mode: null, index_version: null };
      turns.push({ index: e.turn_index, recorded: d.recorded, mode: d.mode, message: d.message ?? '', indexVersion: d.index_version, startedAt: at,
                   endedAt: null, waitMs: 0, waitingSince: null, state: 'running', segments: [], plans: [], fidelity: null,
                   committedVersion: null, error: null });
      continue;
    }
    const turn = current();
    if (!turn) continue;
    switch (e.event) {
      case 'text': {
        const last = turn.segments[turn.segments.length - 1];
        if (last?.kind === 'text') last.text += e.data;
        else turn.segments.push({ kind: 'text', text: e.data });
        break;
      }
      case 'tool_call': {
        const d = parse<{ tool_call_id: string; tool_name: string; input?: Record<string, unknown> }>(e.data);
        if (!d) break;
        lastSteps(turn).push({ id: d.tool_call_id, name: d.tool_name, label: toolLabel(d.tool_name), target: toolTarget(d.tool_name, d.input ?? {}),
                               status: 'running', server: true, startedAt: at, ms: null, error: null, input: d.input ?? {} });
        break;
      }
      case 'tool_result': {
        const d = parse<{ tool_call_id: string; result?: { error?: unknown } }>(e.data);
        const step = d ? findStep(turn, d.tool_call_id) : undefined;
        if (step) settle(step, d?.result?.error ? 'error' : 'done', at, null, d?.result?.error ? String(d.result.error) : null);
        break;
      }
      case 'client_tool_call': {
        const d = parse<{ calls?: { tool_call_id: string; tool_name: string; input?: Record<string, unknown>; requires_confirmation?: boolean }[] }>(e.data);
        const steps = lastSteps(turn);
        for (const c of d?.calls ?? []) {
          steps.push({ id: c.tool_call_id, name: c.tool_name, label: toolLabel(c.tool_name), target: toolTarget(c.tool_name, c.input ?? {}),
                       status: c.requires_confirmation || c.tool_name === 'ask_user' ? 'waiting' : 'pending',
                       server: SERVER_TOOL_NAMES.has(c.tool_name), startedAt: at, ms: null, error: null, input: c.input ?? {} });
        }
        break;
      }
      case 'batch_done': {
        const d = parse<{ results?: { tool_call_id: string; status: StepStatus; ms: number; error?: string | null }[] }>(e.data);
        for (const r of d?.results ?? []) {
          const step = findStep(turn, r.tool_call_id);
          if (step) settle(step, r.status, at, r.ms, r.error ?? null);
        }
        break;
      }
      case 'plan': {
        const d = parse<Plan>(e.data);
        if (d) turn.plans.push(d);
        break;
      }
      case 'waiting':
        turn.state = 'waiting';
        turn.waitingSince = at;
        break;
      case 'answered':
        if (turn.waitingSince != null) turn.waitMs += at - turn.waitingSince;
        turn.waitingSince = null;
        turn.state = 'running';
        break;
      case 'fidelity':
        turn.fidelity = parse<Fidelity>(e.data);
        break;
      case 'committed':
        turn.committedVersion = parse<{ version: number }>(e.data)?.version ?? null;
        break;
      case 'error':
        turn.error = e.data;
        break;
      case 'turn_done':
        turn.state = 'done';
        turn.endedAt = at;
        break;
      case 'failed':
        turn.state = 'failed';
        turn.endedAt = at;
        turn.error = e.data || turn.error;
        break;
      case 'cancelled':
        turn.state = 'cancelled';
        turn.endedAt = at;
        break;
      default:
        break;
    }
  }
  // A step still running when its turn ended did not end in the stream: its turn's end is its end.
  for (const turn of turns) {
    if (turn.endedAt == null) continue;
    for (const seg of turn.segments) {
      if (seg.kind !== 'steps') continue;
      for (const s of seg.steps) if (s.status === 'running' || s.status === 'pending') settle(s, turn.state === 'done' ? 'done' : 'cancelled', turn.endedAt);
    }
  }
  return turns;
}

/** How long the agent has worked on the turn: start to end (or `now`), less the time it waited on staff. */
export function turnMs(turn: SandboxTurn, now: number): number {
  const end = turn.endedAt ?? now;
  const waiting = turn.waitingSince != null ? end - turn.waitingSince : 0;
  return Math.max(0, end - turn.startedAt - turn.waitMs - waiting);
}

/** What the agent is doing now, as the add-in's activity line says it (`MessageBubble.turnPhase`). */
export function activity(turn: SandboxTurn): Activity {
  const steps = turn.segments.flatMap((s) => (s.kind === 'steps' ? s.steps : []));
  if (steps.some((s) => !s.server && (s.status === 'pending' || s.status === 'running'))) return 'excel';
  const last = [...steps].reverse().find((s) => s.status !== 'waiting');
  if (last && last.status === 'running' && PLAN_TOOLS.has(last.name)) return 'planning';
  if (last && last.status === 'running' && READ_TOOLS.has(last.name)) return 'reading';
  if (last && PLAN_TOOLS.has(last.name) && last.status === 'done' && last.name !== 'submit_change_plan') return 'planning';
  return 'thinking';
}

/** "8 s", "1 min 05 s" — the add-in's format (`TurnClock.formatDuration`). */
export function formatDuration(ms: number): string {
  const s = Math.floor(ms / 1000);
  return s < 60 ? `${s} s` : `${Math.floor(s / 60)} min ${String(s % 60).padStart(2, '0')} s`;
}

/** A step's duration, finer than a turn's: "0.4 s", "12 s". */
export function formatStep(ms: number): string {
  return ms < 10_000 ? `${(ms / 1000).toFixed(1)} s` : formatDuration(ms);
}
